/**
 * Product Mix Report Page
 *
 * Main page for the product mix report with date range filter and data table.
 * Shows per-product sales analysis: qty, CA net, FC theorique, FC%.
 * @see Sprint - Rapport Product Mix
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PieChart, AlertTriangle, ShoppingBag, Hash, DollarSign, Percent } from 'lucide-react';
import { Skeleton, EmptyState, KPICard } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import { useCurrency, useCanSeeFinancials, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { useProductMix } from '../hooks/useProductMix';
import { ProductMixFilters } from '../components/ProductMixFilters';
import { ProductMixTable } from '../components/ProductMixTable';
import type { ProductMixReportInput } from '@bstock/shared';

/**
 * Convert DateRange to date strings for API.
 */
function dateRangeToParams(range: DateRange): { dateFrom: string; dateTo: string } | null {
  if (!range.start || !range.end) return null;

  const formatDate = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    dateFrom: formatDate(range.start),
    dateTo: formatDate(range.end),
  };
}

/**
 * Parse URL params for deep linking
 */
function parseUrlParams(searchParams: URLSearchParams): {
  dateRange: DateRange;
} {
  const dateFrom = searchParams.get('dateFrom');
  const dateTo = searchParams.get('dateTo');

  // Default to current month if no dates
  const now = new Date();
  const defaultStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const defaultEnd = now;

  return {
    dateRange: {
      start: dateFrom ? new Date(dateFrom) : defaultStart,
      end: dateTo ? new Date(dateTo) : defaultEnd,
    },
  };
}

export default function ProductMixPage() {
  const currency = useCurrency();
  const showFinancials = useCanSeeFinancials();
  const { formatDateTime } = useFormatDate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL params for deep linking
  const urlParams = useMemo(() => parseUrlParams(searchParams), [searchParams]);

  // Filter state
  const [dateRange, setDateRange] = useState<DateRange>(urlParams.dateRange);

  // Update URL when filters change
  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Build query params
  const queryParams = useMemo<ProductMixReportInput | null>(() => {
    return dateRangeToParams(dateRange);
  }, [dateRange]);

  // Fetch data
  const { data: reportData, isLoading, error } = useProductMix(queryParams);

  // Handlers
  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      setDateRange(range);
      const dates = dateRangeToParams(range);
      if (dates) {
        updateUrl({
          dateFrom: dates.dateFrom,
          dateTo: dates.dateTo,
        });
      }
    },
    [updateUrl]
  );

  // Determine if we have valid dates
  const hasValidDates = dateRange.start && dateRange.end;

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <PieChart className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-dark-500">Product Mix</h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              Analyse des ventes par article : quantite, chiffre d'affaires et food cost
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <ProductMixFilters
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
        />
      </div>

      {/* Content */}
      <div className="p-6 bg-neutral-50">
        {!hasValidDates ? (
          <EmptyState
            icon={PieChart}
            title="Selectionnez une periode"
            description="Choisissez une date de debut et de fin pour generer le rapport"
          />
        ) : isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-20" />
            <Skeleton className="h-96" />
          </div>
        ) : error ? (
          <EmptyState
            icon={AlertTriangle}
            title="Erreur de chargement"
            description="Une erreur est survenue lors du chargement du rapport"
          />
        ) : reportData ? (
          <div className="space-y-6">
            {/* Summary KPIs */}
            <div className={`grid gap-4 ${showFinancials ? 'md:grid-cols-5' : 'md:grid-cols-2'}`}>
              <KPICard
                title="Articles"
                value={reportData.rows.length}
                icon={<ShoppingBag className="w-5 h-5" />}
              />
              <KPICard
                title="Qte vendue"
                value={reportData.totals.quantitySold.toLocaleString('fr-FR')}
                icon={<Hash className="w-5 h-5" />}
              />
              {showFinancials && (
                <>
                  <KPICard
                    title="CA net HT"
                    value={formatCentimes(reportData.totals.revenueNetCentimes, currency)}
                    icon={<DollarSign className="w-5 h-5" />}
                  />
                  <KPICard
                    title="FC theorique HT"
                    value={formatCentimes(reportData.totals.theoreticalCostCentimes, currency)}
                    subtitle={`${reportData.totals.foodCostPercent.toFixed(2)}% du CA`}
                    icon={<Percent className="w-5 h-5" />}
                  />
                  <KPICard
                    title="FC %"
                    value={`${reportData.totals.foodCostPercent.toFixed(2)}%`}
                    icon={<Percent className="w-5 h-5" />}
                  />
                </>
              )}
            </div>

            {/* Data Table */}
            <ProductMixTable
              rows={reportData.rows}
              isLoading={false}
              showFinancials={showFinancials}
            />

            {/* Meta info */}
            <div className="text-xs text-neutral-400 text-right pb-4">
              {reportData.rows.length} articles |
              Periode: {reportData.period.dateFrom} - {reportData.period.dateTo} |
              Genere le {formatDateTime(new Date().toISOString())}
            </div>
          </div>
        ) : (
          <EmptyState
            icon={PieChart}
            title="Aucune donnee"
            description="Aucune donnee disponible pour cette periode"
          />
        )}
      </div>
    </div>
  );
}
